// Global data's Drive connection: one Google account and one shared drive, the global
// folders and tracked files added from it, and the dialog that adds more.
import { useContext, useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";

import {
  GLOBAL_DRIVE_CONNECTION_ID,
  useAddGlobalDriveSources,
  useConnectGlobalDrive,
  useDisconnectGlobalDrive,
  useGlobalDriveBrowseSource,
  useGlobalDriveConnection,
  useSyncGlobalDriveFolder,
} from "../api";
import type { GlobalDataRecord } from "../api";
import { isOperationFinished, useAsyncOperation } from "../api/hooks/useAsyncOperation";
import { useGoogleAuthStatus, useSharedDrives } from "../api/hooks/useDriveSync";
import { QK } from "../api/queryKeys";
import { UserContext } from "../auth";
import { DriveAddSourcesDialog } from "../components/DriveAddSourcesDialog";
import type { DriveAddSelection } from "../components/DriveAddSourcesDialog";
import ds from "../components/DriveSyncSettingsCard.module.css";
import { ProgressBar } from "../components/ProgressBar";
import { SharedDrivePicker } from "../components/SharedDrivePicker";
import { formatRelative } from "../utils/formatRelative";
import { useConnectGoogle } from "../utils/useConnectGoogle";
import { GlobalDriveFolderRow } from "./GlobalDriveFolderRow";
import { GlobalDriveTrackedFileRow } from "./GlobalDriveTrackedFileRow";
import { GlobalKnowledgeBasesSection } from "./GlobalKnowledgeBasesSection";

const messageOf = (err: unknown): string =>
  err instanceof Error ? err.message : String(err);

interface Props {
  folders: GlobalDataRecord[];
  foldersLoading: boolean;
}

export const GlobalDriveConnectionCard = ({ folders, foldersLoading }: Props) => {
  const { user } = useContext(UserContext);
  const authStatus = useGoogleAuthStatus();
  const { connect: connectGoogle, connecting } = useConnectGoogle(
    user?.email ?? undefined,
    { forAgentDocuments: true },
  );
  const queryClient = useQueryClient();
  const [filesOpKey, setFilesOpKey] = useState<string | null>(null);
  const filesSyncing = filesOpKey !== null;
  const connectionQuery = useGlobalDriveConnection({ poll: filesSyncing });
  const filesOp = useAsyncOperation(filesOpKey, { enabled: filesSyncing });
  const connectDrive = useConnectGlobalDrive();
  const disconnect = useDisconnectGlobalDrive();
  const addSources = useAddGlobalDriveSources();
  const syncNow = useSyncGlobalDriveFolder();
  const browseSource = useGlobalDriveBrowseSource();
  const [driveId, setDriveId] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const connection = connectionQuery.data;
  const connected = connection?.connected ?? false;
  const allFiles = connection?.files ?? [];
  // Client knowledge bases are agents' and get their own section below.
  const files = allFiles.filter((f) => f.knowledge_base === null);
  const auth = authStatus.data?.authorization ?? null;
  const sharedDrives = useSharedDrives(auth !== null && connectionQuery.isSuccess && !connected);
  const driveLabel = connection?.drive_name || "Shared drive";
  const authorizerEmail = connection?.authorized_user_email ?? "";
  const isAuthorizer =
    auth !== null &&
    authorizerEmail !== "" &&
    auth.user_email.toLowerCase() === authorizerEmail.toLowerCase();
  const actorName =
    [user?.given_name, user?.family_name].filter(Boolean).join(" ") || user?.email || "";

  // A finished key can linger on the connection state until it refetches; don't resume it.
  const finishedOpKeys = useRef(new Set<string>());
  const resumableOpKey = connection?.active_sync_operation_key ?? null;
  useEffect(() => {
    if (resumableOpKey && filesOpKey === null && !finishedOpKeys.current.has(resumableOpKey)) {
      setFilesOpKey(resumableOpKey);
    }
  }, [resumableOpKey, filesOpKey]);

  useEffect(() => {
    if (!filesOp.data || filesOpKey === null) return;
    const { status, error: opError } = filesOp.data;
    if (!isOperationFinished(status)) return;
    finishedOpKeys.current.add(filesOpKey);
    void queryClient.invalidateQueries([QK.globalDriveConnection]);
    void queryClient.invalidateQueries([QK.globalDriveFiles]);
    if (status === "failed") setError(`Sync failed: ${opError ?? "unknown error"}`);
    setFilesOpKey(null);
  }, [filesOp.data, filesOpKey, queryClient]);

  const startFilesSync = async () => {
    const resp = await syncNow.mutateAsync({ global_data_id: GLOBAL_DRIVE_CONNECTION_ID });
    setFilesOpKey(resp.operation_key);
  };

  const onSyncFiles = async () => {
    setError(null);
    setNotice(null);
    try {
      await startFilesSync();
    } catch (e) {
      setError(`Starting the sync failed: ${messageOf(e)}`);
    }
  };

  const onConnect = async () => {
    setError(null);
    try {
      await connectDrive.mutateAsync({ drive_id: driveId });
      setDriveId("");
    } catch (e) {
      setError(messageOf(e) || "Connecting the shared drive failed.");
    }
  };

  const onDisconnect = () => {
    const ok = window.confirm(
      `Disconnect global data from ${driveLabel}? Every project's agents lose the global files until someone connects again. Folders and files stay listed, and reconnecting the same drive restores them.`,
    );
    if (!ok) return;
    setError(null);
    setNotice(null);
    disconnect.mutate();
  };

  // New syncs re-attach from the refreshed state: folders' from the global list, the
  // tracked files' from the connection's active_sync_operation_key.
  const onAdd = async ({ folderIds, fileIds }: DriveAddSelection) => {
    const resp = await addSources.mutateAsync({
      folder_ids: folderIds,
      file_ids: fileIds,
      actor_name: actorName,
    });
    setAddOpen(false);
    setError(null);
    const folders = resp.awaiting_review_ids.length;
    if (folders > 0) {
      setNotice(
        `Added ${folders} folder${folders === 1 ? "" : "s"}. Nothing is downloaded yet: open ${folders === 1 ? "it" : "each"} below, untick the subfolders and files global data should not hold, then press Start first sync.`,
      );
      return;
    }
    if (resp.operation_keys.length === 0) {
      setNotice("Everything you picked is already in global data.");
      return;
    }
    setNotice(null);
  };

  const loading = connectionQuery.isLoading || authStatus.isLoading;

  return (
    <section className={ds.card}>
      <header className={ds.header}>
        <h3 className={ds.title}>Connected drive</h3>
        {connected && (
          <span className={ds.authorizedAs}>
            Synced through <strong>{authorizerEmail}</strong>
            {isAuthorizer && (
              <>
                {" "}
                <button
                  type="button"
                  onClick={connectGoogle}
                  disabled={connecting}
                  className={ds.linkButton}
                >
                  {connecting ? "Redirecting…" : "Reconnect"}
                </button>
              </>
            )}
          </span>
        )}
      </header>

      {loading ? (
        <p className={ds.muted}>Loading the Drive connection…</p>
      ) : connectionQuery.isError ? (
        <p className={ds.error}>
          Couldn&apos;t load the Drive connection.{" "}
          <button
            type="button"
            className={ds.linkButton}
            onClick={() => void connectionQuery.refetch()}
          >
            Retry
          </button>
        </p>
      ) : connected ? (
        <>
          <p className={ds.driveName}>
            Shared drive: <strong>{driveLabel}</strong>
          </p>
          {connection?.last_sync_status === "auth_failed" && (
            <div className={ds.warning}>
              The Google connection global data syncs through has expired.{" "}
              {isAuthorizer
                ? "Reconnect to keep syncing."
                : `Ask ${authorizerEmail} to reconnect, or disconnect and connect again with your own Google account.`}
            </div>
          )}
        </>
      ) : !auth ? (
        <div className={ds.field}>
          <p>
            Global data isn&apos;t connected to a shared drive. Connect your Google account,
            then choose the shared drive global folders and files are added from.
          </p>
          <p className={ds.muted}>
            Inrange gets read-only access to your Drive (drive.readonly scope only).
          </p>
          <button
            type="button"
            onClick={connectGoogle}
            disabled={connecting}
            className={ds.primaryButton}
          >
            {connecting ? "Redirecting…" : "Connect Google Drive"}
          </button>
        </div>
      ) : (
        <div className={ds.field}>
          <label className={ds.label} htmlFor="global-drive-shared-drive">
            Shared drive
          </label>
          <SharedDrivePicker
            query={sharedDrives}
            id="global-drive-shared-drive"
            value={driveId}
            onChange={setDriveId}
            placeholder="Choose a shared drive"
            emptyMessageSubject="global data's"
          />
          {(sharedDrives.data?.drives.length ?? 0) > 0 && (
            <>
              <p className={ds.helpText}>
                Once connected, add folders and files from this shared drive with
                &ldquo;+ Add folders or files&rdquo;. Reconnecting the drive global data used
                before restores its folders and files.
              </p>
              <div className={ds.actions}>
                <button
                  type="button"
                  onClick={() => void onConnect()}
                  disabled={!driveId || connectDrive.isLoading}
                  className={ds.primaryButton}
                >
                  {connectDrive.isLoading ? "Connecting…" : "Connect shared drive"}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {connection && (
        <>
          <div className={ds.sectionHeader}>
            <span className={ds.label}>Folders ({folders.length})</span>
            {connected && (
              <button
                type="button"
                className={ds.secondaryButton}
                onClick={() => setAddOpen(true)}
              >
                + Add folders or files
              </button>
            )}
          </div>
          {foldersLoading ? (
            <p className={ds.muted}>Loading folders…</p>
          ) : folders.length === 0 ? (
            <p className={ds.muted}>
              {connected
                ? `No folders yet. Use "+ Add folders or files" to choose them from ${driveLabel}.`
                : "No folders."}
            </p>
          ) : (
            <ul className={ds.sourceList}>
              {folders.map((record) => (
                <GlobalDriveFolderRow
                  key={record.global_data_id}
                  record={record}
                  connected={connected}
                />
              ))}
            </ul>
          )}

          <div className={ds.sectionHeader}>
            <span className={ds.label}>Files ({files.length})</span>
            {connected && (
              <button
                type="button"
                className={ds.secondaryButton}
                onClick={() => void onSyncFiles()}
                disabled={files.length === 0 || filesSyncing || syncNow.isLoading}
              >
                {filesSyncing || syncNow.isLoading ? "Syncing…" : "Sync files now"}
              </button>
            )}
          </div>
          {connection.last_synced_at && (
            <div className={ds.statusLine}>
              Last file sync: <strong>{formatRelative(connection.last_synced_at)}</strong>
              {connection.last_sync_status && (
                <>
                  {" · "}status: <strong>{connection.last_sync_status}</strong>
                </>
              )}
              {connection.last_sync_error && (
                <p className={ds.error}>Error: {connection.last_sync_error}</p>
              )}
            </div>
          )}
          {filesSyncing && filesOp.data?.progress && (
            <div className={ds.syncResult}>
              <ProgressBar progress={filesOp.data.progress} />
            </div>
          )}
          {files.length === 0 ? (
            <p className={ds.muted}>No files are tracked.</p>
          ) : (
            <ul className={ds.sourceList}>
              {files.map((file) => (
                <GlobalDriveTrackedFileRow key={file.file_id} file={file} />
              ))}
            </ul>
          )}

          <GlobalKnowledgeBasesSection
            files={allFiles}
            gaps={connection.knowledge_base_gaps}
          />
        </>
      )}

      {error && (
        <p className={ds.error} role="alert">
          {error}
        </p>
      )}
      {notice && <p className={ds.helpText}>{notice}</p>}
      {connected && (
        <div className={ds.actions}>
          <button
            type="button"
            onClick={onDisconnect}
            disabled={disconnect.isLoading}
            className={ds.dangerButton}
          >
            {disconnect.isLoading ? "Disconnecting…" : "Disconnect"}
          </button>
        </div>
      )}

      {connected && connection && (
        <DriveAddSourcesDialog
          open={addOpen}
          driveName={connection.drive_name}
          destination="global data"
          source={browseSource}
          adding={addSources.isLoading}
          startInFolder={null}
          onAdd={onAdd}
          onClose={() => setAddOpen(false)}
        />
      )}
    </section>
  );
};
